/* eslint-disable unused-imports/no-unused-vars */
import type { TooltipItem } from "chart.js";
import { useCallback, useEffect, useState } from "react";
import { Column as RdgColumn, SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { TransactionsBlueIcon } from "../../../../assets";
import { ColorConstants, ProjectIssuanceConstants, ProjectIssuanceTransactionConstants } from "../../../../constants";
import { CursorChangeProps, DataGridColumnCurrency, ResultType, SelectData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { SearchIssuanceTransactionsResponse } from "../../../../service/query";
import {
  getAggregateIssuances,
  searchInteractions,
  searchIssuanceTransactions,
} from "../../../../service/query/QueryService";
import { ResultData, Status } from "../../../../service/Shared";
import { UnitTransactionsPanelFilterObjects } from "../../../../templates/UnitTransactionsPanel/UnitTransactionsPanel";
import { useAuth } from "../../../../useAuth";
import {
  convertDateToStringDDMMYYYY,
  flattenObject,
  formatNumber,
  jsonReviver,
  recursivelyValidateAndTransformDateToString,
} from "../../../../utils";
import { getLandholdingDashboardRoute, getProjectDetailsByUuid } from "../../../../utils/routes";
import {
  ChartDatasets,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridIconCellFormatterData,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  StackedChartData,
} from "../../../../widget";

export const INVENTORY_STATUS_SELECT_DATA: SelectData = [
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED, value: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED },
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT, value: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT },
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE, value: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE },
];

interface UseInventoryManagementBoardReturnData {
  gridOnChange: DataGridCursorDataLoadEventHandler;
  hasEnoughDataForScrolling: boolean;
  isLoading: boolean;
  dropDownData: SelectData;
  selectedUnitMeasurementData: string;
  selectedStatuses: string[];
  handleStatusFilter: (values: string[]) => void;
  activeSelectOptionText: string;
  setActiveSelectOptionText: (value: string) => void;
  pageData: InventoryManagementBoardPageData;
  kpiData: InventoryManagementBoardPageKpiData;
  columns: DataGridColumnDefinition[];
  initTagsFilterData: SelectData;
  tagsFilterData: SelectData;
  onTagsFilter: (keys: string[]) => Promise<void>;
  tagsFilterSelectedData?: string;
  hasUnits: boolean;
  prefix?: string;
  handleUpdateSelectedUnitMeasurement: (value: string) => void;
  inventorySelectorButtons: string[];
  searchIssuanceTransactionsSortingCriteria: SortColumn[];
  searchIssuanceTransactionsColumns: DataGridColumnDefinition[];
  isSalesOverTimeSelected?: boolean;
  unitTransactionsPanelFilter: UnitTransactionsPanelFilterObjects | undefined;
  handleCloseUnitTransactionsPanel: () => void;
  refresh: boolean;
  handleRefresh: () => void;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];
}

export interface InventoryManagementBoardPageKpiData {
  totalAvailableToSell: number;
  totalReserved: number;
  totalSold: number;
}

export interface InventoryManagementBoardPageData {
  chartData: StackedChartData[] | undefined;
  chartDatasets: ChartDatasets | undefined;
  chartLabels: (string | string[])[];
}

export const inventoryTooltipLabel = (context: TooltipItem<"bar">, prefix?: string): string => {
  const projectName = context.chart.data.labels?.[context.dataIndex];
  return `${context.dataset.label}: ${prefix || ""}${formatNumber(context.parsed.y)} (${projectName})`;
};

const searchIssuanceTransactionsColumns: DataGridColumnDefinition[] = [
  {
    name: "Transaction date",
    key: "transactionDate",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "left",
    sortable: true,
    filterable: false,
  },
  {
    name: "Project",
    key: "projectLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Landholding",
    key: "landholdingLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Verifier",
    key: "verifierName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Quantity",
    key: "adjustedQuantity",
    dataType: "number",
    formatter: "number",
    sortable: true,
    filterable: true,
  },
  {
    name: "Measurement",
    key: "unitMeasurement",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Counterparty",
    key: "counterParty",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Broker",
    key: "broker",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Total value",
    key: "adjustedValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Transaction reference",
    key: "externalReference",
    dataType: "string",
    formatter: "stringWithEllipsis",
    sortable: true,
    filterable: true,
  },
];
const searchIssuanceTransactionsSortingCriteria: SortColumn[] = [{ columnKey: "transactionDate", direction: "ASC" }];

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.transactionsButton !== undefined) delete row.transactionsButton;
    // eslint-disable-next-line no-param-reassign
    if (row.averagePrice !== undefined) row.averagePrice = Number(row.averagePrice).toFixed(2);
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "transactionsButton");
};

export const useInventoryManagementBoard = (): UseInventoryManagementBoardReturnData => {
  const { currentUserType } = useAuth();
  const [dropDownData, setDropDownData] = useState<SelectData>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasEnoughDataForScrolling, setHasEnoughDataForScrolling] = useState(false);
  const [selectedUnitMeasurementData, setSelectedUnitMeasurementData] = useState<string>("");
  const [activeSelectOptionText, setActiveSelectOptionText] = useState<string>("By project");
  const [inventorySelectorButtons, setInventorySelectorButtons] = useState<string[]>([
    "Units per project",
    "Revenue per project",
    "Sales over time",
  ]);
  const [initTagsFilterData, setInitTagsFilterData] = useState<SelectData>([]);
  const [tagsFilterSelectedData, setTagsFilterSelectedData] = useState<string | undefined>();
  const [tagsFilterData, setTagsFilterData] = useState<SelectData>([]);
  const [tagsFilterIssuanceUuids, setTagsFilterIssuanceUuids] = useState<string[]>(); // For sales over time chart
  const [hasUnits, setHasUnits] = useState(false);
  const [unitTransactionsPanelFilter, setunitTransactionsPanelFilter] = useState<UnitTransactionsPanelFilterObjects>();

  const location = useLocation();

  const [kpiData, setKpiData] = useState<InventoryManagementBoardPageKpiData>({
    totalAvailableToSell: 0,
    totalReserved: 0,
    totalSold: 0,
  });
  const [pageData, setPageData] = useState<InventoryManagementBoardPageData>({
    chartData: [],
    chartDatasets: [],
    chartLabels: [],
  });

  const [refresh, setRefresh] = useState(false);

  const handleRefresh = (): void => {
    setRefresh(!refresh);
  };

  const handleCloseUnitTransactionsPanel = (): void => {
    setunitTransactionsPanelFilter(undefined);
  };

  useEffect(() => {
    if (selectedUnitMeasurementData === "") setActiveSelectOptionText("Units per project");
    else setActiveSelectOptionText("By project");
  }, [selectedUnitMeasurementData]);

  const handleUpdateSelectedUnitMeasurement = (value: string): void => {
    setSelectedUnitMeasurementData(value);
    setActiveSelectOptionText(value === "" ? "Units per project" : "By project");
    switch (value) {
      case "Carbon":
        setInventorySelectorButtons(["By project", "By vintage", "By type", "By unit"]);
        break;
      case "Biodiversity":
        setInventorySelectorButtons(["By project", "By habitat group", "By condition"]);
        break;
      case "":
      default:
        setInventorySelectorButtons(["Units per project", "Revenue per project", "Sales over time"]);
    }
  };

  const formatAggregateIssuancesData = useCallback(
    (responseData: ResultData[]): ResultData[] =>
      responseData.map((el) => {
        const result = flattenObject(el);

        if (["Units per project", "Revenue per project", "By project"].some((x) => x === activeSelectOptionText)) {
          result.link = {
            text: el.groupByFieldContent1,
            to: getProjectDetailsByUuid(el.groupByFieldContent2?.toString() || "", currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;
        }

        result.transactionsButton = {
          icon: <TransactionsBlueIcon />,
          action: () =>
            setunitTransactionsPanelFilter({
              projectUuid: ["Units per project", "Revenue per project", "By project"].some(
                (x) => x === activeSelectOptionText
              )
                ? (el.groupByFieldContent2 as string)
                : undefined,
              vintage:
                activeSelectOptionText === "By vintage" ? parseFloat(el.groupByFieldContent1 as string) : undefined,
              projectTypeDisplayName:
                activeSelectOptionText === "By type" ? (el.groupByFieldContent1 as string) : undefined,
              quantityType: activeSelectOptionText === "By unit" ? (el.groupByFieldContent1 as string) : undefined,
              habitatGroup:
                activeSelectOptionText === "By habitat group" ? (el.groupByFieldContent1 as string) : undefined,
              distinctiveness:
                activeSelectOptionText === "By habitat group" ? (el.groupByFieldContent2 as string) : undefined,
              habitatCondition:
                activeSelectOptionText === "By condition" ? (el.groupByFieldContent1 as string) : undefined,
            }),
        } as DataGridIconCellFormatterData;

        if (el.landholdingUuid)
          result.landholdingLink = {
            text: el.landholdingName,
            to: getLandholdingDashboardRoute(result.landholdingUuid as string, currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;

        return result;
      }) || [],
    [activeSelectOptionText]
  );

  const formatIssuanceTransactionsData = useCallback(
    (responseData: SearchIssuanceTransactionsResponse | undefined): ResultData[] => {
      return (
        responseData?.results?.map((d) => {
          const result = flattenObject(d) as ResultData;

          result.projectLink = {
            text: d.project.displayName,
            to: getProjectDetailsByUuid(d.project.uuid, currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;

          if (d.landholding.uuid)
            result.landholdingLink = {
              text: d.landholding.name,
              to: getLandholdingDashboardRoute(d.landholding.uuid, currentUserType),
              state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
            } as DataGridLinkCellFormatterData;

          return result;
        }) || []
      );
    },
    []
  );

  // Common column definitions
  const groupByName1Column: DataGridColumnDefinition = {
    name: "Name1",
    key: "groupByFieldContent1",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const groupByName2Column: DataGridColumnDefinition = {
    name: "Name2",
    key: "groupByFieldContent2",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const groupByName3Column: DataGridColumnDefinition = {
    name: "Name3",
    key: "groupByFieldContent3",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const sumAreaHaColumn: DataGridColumnDefinition = {
    name: "Unit size (Ha)",
    key: "areaHa",
    dataType: "number",
    filterable: true,
    sortable: true,
  };

  const unitKmColumn: DataGridColumnDefinition = {
    name: "Unit size (Km)",
    key: "unitKm",
    dataType: "number",
    filterable: true,
    sortable: true,
  };

  const totalValueColumn: DataGridColumnDefinition = {
    name: "Total value",
    key: "totalValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  };

  const landholdingColumn: DataGridColumnDefinition = {
    name: "Landholding",
    key: "landholdingLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  };

  const verifierColumn: DataGridColumnDefinition = {
    name: "Verifier",
    key: "verifierName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  const commonColumns: DataGridColumnDefinition[] = [
    { name: "Available", key: "availableUnits", dataType: "number", filterable: true, sortable: true },
    { name: "Reserved", key: "reservedUnits", dataType: "number", filterable: true, sortable: true },
    { name: "Sold", key: "soldUnits", dataType: "number", filterable: true, sortable: true },
    {
      name: "% sold",
      key: "percentageSold",
      formatter: "percentage",
      dataType: "number",
      filterable: true,
      sortable: true,
    },
    {
      name: "Avg. sale price",
      key: "averagePrice",
      dataType: "number",
      formatter: "currency",
      currency: DataGridColumnCurrency.Gbp,
      filterable: true,
      sortable: true,
    },
    {
      name: "Transactions",
      key: "transactionsButton",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
    },
  ];

  const columnsDict: Record<string, { columns: DataGridColumnDefinition[]; insertColumnFunction?: () => void }> = {
    "|Units per project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        { ...groupByName3Column, name: "Measurement" },
      ],
    },
    "|Revenue per project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        { ...groupByName3Column, name: "Measurement" },
      ],
      insertColumnFunction: () => {
        commonColumns.splice(5, 0, totalValueColumn);
      },
    },
    "|Sales over time": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        { ...groupByName3Column, name: "Measurement" },
      ],
    },
    "Carbon|By project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
      ],
    },
    "Carbon|By vintage": {
      columns: [{ ...groupByName1Column, name: "Vintage" }],
    },
    "Carbon|By type": {
      columns: [{ ...groupByName1Column, name: "Project type" }],
    },
    "Carbon|By unit": {
      columns: [{ ...groupByName1Column, name: "Quantity type" }],
    },
    "Carbon|By region": {
      columns: [{ ...groupByName1Column, name: "Region" }],
    },
    "Biodiversity|By project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By habitat group": {
      columns: [
        { ...groupByName1Column, name: "Habitat group" },
        { ...groupByName2Column, name: "Distinctiveness" },
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By condition": {
      columns: [{ ...groupByName1Column, name: "Habitat condition" }, sumAreaHaColumn, unitKmColumn],
    },
    "Biodiversity|By region": {
      columns: [{ ...groupByName1Column, name: "Region" }, sumAreaHaColumn, unitKmColumn],
    },
  };

  const gridGetColumns = (
    unitMeasurement: string | undefined | null,
    selectOptionText: string | undefined | null
  ): DataGridColumnDefinition[] => {
    const dictKey = `${unitMeasurement || ""}|${selectOptionText || ""}`;
    const columnsDictEntry = columnsDict[dictKey];

    // Insert any additional columns into commonColumns if needed
    if (columnsDictEntry?.insertColumnFunction) columnsDictEntry.insertColumnFunction();

    return [...(columnsDictEntry?.columns || []), ...commonColumns];
  };

  const optionDict: { [key: string]: string } = {
    "Units per project": "projectNameMeasurement",
    "Revenue per project": "projectNameMeasurement",
    "By project": "projectName",
    "By habitat group": "habitat",
    "By condition": "habitatCondition",
    "By region": selectedUnitMeasurementData === "Biodiversity" ? "regionAreaHa" : "region",
    "By vintage": "vintageStartDate",
    "By type": "projectType",
    "By unit": "quantityType",
  };
  const getGroupCondition = (optionText: string): string => {
    if (optionDict[optionText] === undefined && selectedUnitMeasurementData === "") {
      return selectedUnitMeasurementData === "" ? "projectNameMeasurement" : "projectName";
    }
    return optionDict[optionText];
  };

  const gridOnChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (activeSelectOptionText === "Sales over time") {
      if (filterCriteria.projectLink) {
        filterCriteria.project = {
          displayName: filterCriteria.projectLink,
        };
        filterCriteria.projectLink = undefined;
      }

      if (filterCriteria.landholdingLink) {
        filterCriteria.landholding = {
          name: filterCriteria.landholdingLink,
        };
        filterCriteria.landholdingLink = undefined;
      }
      if (tagsFilterIssuanceUuids && tagsFilterIssuanceUuids.length > 0) {
        filterCriteria.issuanceUuid = {
          operator: "in",
          value: tagsFilterIssuanceUuids,
        };
      }

      if (selectedStatuses.length > 0) {
        filterCriteria.issuanceStatus = {
          operator: "in",
          value: selectedStatuses,
        };
      }

      filterCriteria.type = {
        operator: "eq",
        value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
      };

      if (!filterCriteria.adjustedQuantity) {
        filterCriteria.adjustedQuantity = {
          operator: "neq",
          value: 0,
        };
      }

      const sortKeyMap: Record<string, string> = {
        projectLink: "project.displayName",
        landholdingLink: "landholding.name",
      };

      await searchIssuanceTransactions({
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: sortKeyMap[s.key] || s.key,
          direction: s.direction,
        })),
        filter: { results: filterCriteria },
      })
        .then((response) => {
          if (response.status === Status.Success && response.data) {
            data = {
              resultData: formatIssuanceTransactionsData(response.data),
              paging: {
                startCursor: response.data?.paging?.startCursor || "",
                endCursor: response.data?.paging?.endCursor || "",
                pageSize: paging.pageSize || 10,
                totalCount: response.data?.paging?.total || 0,
                hasNextPage: response.data?.paging?.hasNextPage || false,
                hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
              },
            };

            const chartData = response.data.results.reduce(
              (
                acc: {
                  cumulative: { carbon: number; biodiversity: number };
                  byDate: Record<string, { carbon: number; biodiversity: number }>;
                },
                item
              ) => {
                const dateStr = convertDateToStringDDMMYYYY(item.transactionDate);

                if (!acc.byDate[dateStr]) {
                  acc.byDate[dateStr] = {
                    carbon: acc.cumulative.carbon,
                    biodiversity: acc.cumulative.biodiversity,
                  };
                }

                if (item.unitMeasurement === "Carbon") {
                  acc.cumulative.carbon += item.adjustedValue ?? 0;
                } else if (item.unitMeasurement === "Biodiversity") {
                  acc.cumulative.biodiversity += item.adjustedValue ?? 0;
                }

                // Always update both values
                acc.byDate[dateStr].carbon = acc.cumulative.carbon;
                acc.byDate[dateStr].biodiversity = acc.cumulative.biodiversity;

                return acc;
              },
              {
                cumulative: { carbon: 0, biodiversity: 0 },
                byDate: {},
              }
            ).byDate;
            setHasUnits(response.data.results.length > 0);
            setHasEnoughDataForScrolling(response.data.results.length >= 5);

            const chartLabelsArray = Object.keys(chartData).sort(
              (a, b) => new Date(a).getTime() - new Date(b).getTime()
            );

            const biodiversityData = chartLabelsArray.map((date) => chartData[date].biodiversity ?? 0);
            const carbonData = chartLabelsArray.map((date) => chartData[date].carbon ?? 0);

            setPageData({
              chartDatasets: [
                {
                  label: "Biodiversity",
                  data: biodiversityData,
                },
                {
                  label: "Carbon",
                  data: carbonData,
                },
              ],
              chartData: undefined,
              chartLabels: chartLabelsArray,
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (sorting.length === 0) {
        sorting.push({ key: "groupByFieldContent1", direction: "asc" });
      }

      const filterCondition = [];
      if (selectedUnitMeasurementData) {
        filterCondition.push({
          filterBy: "measurementType",
          filterOperator: "eq",
          filterValue: selectedUnitMeasurementData,
        });
      }
      if (selectedStatuses.length > 0) {
        filterCondition.push({
          filterBy: "issuanceStatus",
          filterOperator: "in",
          filterValue: `[${selectedStatuses.join(",")}]`,
        });
      }
      if (tagsFilterSelectedData) {
        filterCondition.push({
          filterBy: "tag",
          filterOperator: "in",
          filterValue: `[${tagsFilterSelectedData}]`,
        });
      }

      if (filterCriteria.landholdingLink) {
        filterCriteria.landholdingName = filterCriteria.landholdingLink;
        delete filterCriteria.landholdingLink;
      }

      if (filterCriteria.link) {
        filterCriteria.groupByFieldContent1 = filterCriteria.link;
        delete filterCriteria.link;
      }

      const sortKeyMap: Record<string, string> = {
        link: "groupByFieldContent1",
        landholdingLink: "landholdingName",
      };
      await getAggregateIssuances({
        filters: filterCondition,
        groupBy: [getGroupCondition(activeSelectOptionText)],
        isReadableProject: true,
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: sortKeyMap[s.key] || s.key,
          direction: s.direction,
        })),
        filter: { results: filterCriteria },
      })
        .then((response) => {
          if (response.status === Status.Success && response.data) {
            const dropDownSelectData = response.data.aspects.measurementTypes
              .filter((measurementType: string) => measurementType !== "Ecosystem recovery")
              .map((v: string) => ({
                key: v,
                value: v,
              })) as SelectData;
            dropDownSelectData.unshift({ key: "", value: "Overall" });

            const carbonIdx = dropDownSelectData.findIndex((item) => item.key === "Carbon");
            if (carbonIdx > 1) {
              const [carbonItem] = dropDownSelectData.splice(carbonIdx, 1);
              dropDownSelectData.splice(1, 0, carbonItem);
            }

            setDropDownData(dropDownSelectData);
            const chartLabelsArray: (string | string[])[] = [];
            const gridData: ResultData[] = [];
            response.data.results.forEach((result) => {
              let nameValue: any = result.groupByFieldContent1 ?? "";
              if (nameValue instanceof Date) {
                nameValue = nameValue.getFullYear().toString();
              }

              let chartLabel = nameValue;
              if (activeSelectOptionText === "By habitat group") {
                chartLabel = [result.groupByFieldContent1, result.groupByFieldContent2];
              }

              gridData.push({
                groupByFieldContent1: nameValue,
                groupByFieldContent2: result.groupByFieldContent2,
                groupByFieldContent3: result.groupByFieldContent3,
                availableUnits: Number(result.availableUnits ?? 0),
                reservedUnits: Number(result.reservedUnits ?? 0),
                soldUnits: Number(result.soldUnits ?? 0),
                percentageSold: Number(result.percentageSold.toFixed(2) ?? 0),
                averagePrice: Number(result.averagePrice ?? 0),
                areaHa: Number(result.areaHa ?? 0),
                unitKm: Number(result.unitKm ?? 0),
                totalValue: Number(result.totalValue ?? 0),
                landholdingName: result.landholdingName,
                landholdingUuid: result.landholdingUuid,
                verifierName: result.verifierName,
              });
              chartLabelsArray.push(chartLabel);
            });

            const formatted = formatAggregateIssuancesData(response.data.results);
            // Set isUnits to true if there are any Units, false otherwise
            setHasUnits(formatted.length > 0);

            setPageData({
              chartDatasets: undefined,
              chartData:
                activeSelectOptionText === "Revenue per project"
                  ? [
                      {
                        label: "Revenue",
                        data: gridData.map((item) => item.totalValue as number),
                        backgroundColor: ColorConstants.GREEN_600,
                        hoverBackgroundColor: ColorConstants.GREEN_500,
                      },
                    ]
                  : [
                      {
                        label: "Available",
                        data: gridData.map((item) => item.availableUnits as number),
                        backgroundColor:
                          selectedUnitMeasurementData === "Carbon"
                            ? ColorConstants.INDIGO_100
                            : ColorConstants.GREEN_200,
                        hoverBackgroundColor:
                          selectedUnitMeasurementData === "Carbon" ? "#E7E6F2" : ColorConstants.GREEN_100,
                      },
                      {
                        label: "Reserved",
                        data: gridData.map((item) => item.reservedUnits as number),
                        backgroundColor:
                          selectedUnitMeasurementData === "Carbon"
                            ? ColorConstants.INDIGO_300
                            : ColorConstants.GREEN_400,
                        hoverBackgroundColor:
                          selectedUnitMeasurementData === "Carbon"
                            ? ColorConstants.INDIGO_200
                            : ColorConstants.GREEN_300,
                      },
                      {
                        label: "Sold",
                        data: gridData.map((item) => item.soldUnits as number),
                        backgroundColor:
                          selectedUnitMeasurementData === "Carbon"
                            ? ColorConstants.INDIGO_500
                            : ColorConstants.GREEN_600,
                        hoverBackgroundColor:
                          selectedUnitMeasurementData === "Carbon"
                            ? ColorConstants.INDIGO_400
                            : ColorConstants.GREEN_500,
                      },
                    ],
              chartLabels: chartLabelsArray,
            });
            data.resultData.push(...formatAggregateIssuancesData(gridData));

            data.paging = {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            };

            setKpiData({
              totalAvailableToSell: response.data.aspects.totalAvailableUnits || 0,
              totalReserved: response.data.aspects.totalReservedUnits || 0,
              totalSold: response.data.aspects.totalSoldUnits || 0,
            });

            const aspectsTags = (response.data.aspects.tags || []).map((v) => ({
              key: `${v.tagUuid}`,
              value: JSON.parse(v.tagData)?.name,
            }));
            setTagsFilterData(aspectsTags);
            if (tagsFilterData.length === 0) setInitTagsFilterData(aspectsTags);

            setHasEnoughDataForScrolling(response.data.results.length >= 5);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return data;
  };

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    if (activeSelectOptionText === "Sales over time") {
      if (keys.length === 0) {
        setTagsFilterIssuanceUuids(undefined);
      } else {
        const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

        searchInteractionsFilterCriteria.uuid = {
          operator: "in",
          value: keys,
        };

        await searchInteractions({
          paging: {
            beforeCursor: null,
            afterCursor: null,
            limit: 50,
          },
          filter: { results: searchInteractionsFilterCriteria },
        }).then((response) => {
          if (response.data?.results != null && response.data.results.length > 0) {
            setTagsFilterIssuanceUuids(
              response.data.results
                .filter((x) => x.objectInteractions.length > 0)
                .map((x) => x.objectInteractions.map((y) => y.objectUuid))
                .flat()
            );
          }
        });
      }

      setTagsFilterSelectedData(undefined);
      handleRefresh();
    } else {
      setTagsFilterSelectedData(keys.length > 0 ? keys.join(",") : undefined);
      setTagsFilterIssuanceUuids(undefined);
    }
  };

  return {
    hasUnits,
    gridOnChange,
    isLoading,
    hasEnoughDataForScrolling,
    dropDownData,
    selectedUnitMeasurementData,
    selectedStatuses,
    handleStatusFilter: setSelectedStatuses,
    activeSelectOptionText,
    setActiveSelectOptionText,
    pageData,
    kpiData,
    columns: gridGetColumns(selectedUnitMeasurementData, activeSelectOptionText),
    initTagsFilterData,
    tagsFilterData,
    onTagsFilter,
    tagsFilterSelectedData,
    prefix: activeSelectOptionText === "Revenue per project" ? "£" : undefined,
    inventorySelectorButtons,
    handleUpdateSelectedUnitMeasurement,
    searchIssuanceTransactionsSortingCriteria,
    searchIssuanceTransactionsColumns,
    isSalesOverTimeSelected: activeSelectOptionText === "Sales over time",
    unitTransactionsPanelFilter,
    handleCloseUnitTransactionsPanel,
    refresh,
    handleRefresh,
    filterExportColumns,
    filterExportRows,
  };
};
